import type { Quote } from "@fprint/shared";

import { declineReason } from "../lib/quoteInfo";

/**
 * Friendly panels for the multistage quote statuses that are NOT payable:
 * `in_review`, `needs_manual_quote` and `declined`. Shared by the quote page
 * and the order page — neither may ever render a pay button for these.
 */
export function QuoteStatusNotice({ quote }: { quote: Quote }) {
  const lockedQty =
    quote.submitted_qty != null ? (
      <p className="mt-1 text-xs text-muted-fg">
        Quantity locked at {quote.submitted_qty}{" "}
        {quote.submitted_qty === 1 ? "part" : "parts"}.
      </p>
    ) : null;

  if (quote.status === "in_review") {
    return (
      <div
        role="status"
        data-testid="quote-in-review"
        className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
      >
        <p className="font-medium">Your quote is being reviewed</p>
        <p className="mt-1">
          Our operator is checking this job. We&apos;ll email you the final
          quote — no payment is needed until then.
        </p>
        {lockedQty}
      </div>
    );
  }

  if (quote.status === "needs_manual_quote") {
    return (
      <div
        role="status"
        data-testid="quote-needs-manual"
        className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
      >
        <p className="font-medium">Awaiting a manual quote</p>
        <p className="mt-1">
          This job is priced personally by our operator. We&apos;ll email you
          the quote as soon as it&apos;s ready.
        </p>
        {lockedQty}
      </div>
    );
  }

  if (quote.status === "declined") {
    const reason = declineReason(quote.result);
    return (
      <div
        role="alert"
        data-testid="quote-declined"
        className="rounded-md border border-danger bg-danger/10 px-4 py-3 text-sm text-danger-fg"
      >
        <p className="font-medium">We can&apos;t take on this job</p>
        <p className="mt-1">
          {reason ??
            "Unfortunately we had to decline this request. Feel free to get in touch or try a different model."}
        </p>
      </div>
    );
  }

  return null;
}
